/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Loading 클래스를 정의한다.
 *
 * @file /src/components/loading/Loading.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 30.
 */
import * as parent from '@base/Component';
import * as loading from '@elements/loading/Loading';
import { LanguageText } from '@utils/Language';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 로딩아이콘 타입
     */
    indicator?: loading.IndicatorType;

    /**
     * 로딩아이콘 및 로딩메시지의 위치방향
     */
    direction?: 'column' | 'row';

    /**
     * 로딩메시지
     */
    text?: LanguageText;

    /**
     * 백그라운드 비활성화효과를 추가할지 여부
     */
    background?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Loading extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'loading';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'loading';

    /**
     * 로딩표시 타입
     */
    indicator: loading.IndicatorType = 'column';

    /**
     * 로딩아이콘 및 로딩메시지의 위치방향
     */
    direction: 'column' | 'row' = 'row';

    /**
     * 로딩메시지
     */
    text: LanguageText = null;

    /**
     * 백그라운드 비활성화효과를 추가할지 여부
     */
    background: boolean = true;

    /**
     * 로딩 엘리먼트
     */
    loading: loading.Loading = null;

    /**
     * 로딩 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Loading> = null) {
        super(properties);

        this.indicator = this.properties.indicator ?? 'column';
        this.direction = this.properties.direction ?? 'column';
        this.text = this.properties.text ?? null;
        this.background = this.properties.background !== false;
    }

    /**
     * 로딩 엘리먼트를 가져온다.
     *
     * @returns 엘리먼트
     */
    getLoading(): loading.Loading {
        if (this.loading === null) {
            this.loading = new loading.Loading({
                indicator: this.indicator,
                direction: this.direction,
                text: this.text,
                background: this.background,
            });
            this.loading.setComponent(this);
        }

        return this.loading;
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        this.$getContent().append(this.getLoading().$getElement());
        await this.getLoading().render();

        this.$getContainer().append(this.$getContent());
    }
}
